import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { MapPin, MessageCircle, ArrowRight, Activity, Ear } from "lucide-react";
import { HeroLine } from "../Reveal";
import { STOCK, CLINIC, whatsappLink } from "../../data/site";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y1 = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-dash-bg pt-[74px]" data-testid="home-hero">
      {/* ambient background */}
      <div className="pointer-events-none absolute -top-20 right-0 h-[520px] w-[520px] rounded-full bg-dash-teal/10 blur-3xl" />
      <div className="pointer-events-none absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-dash-indigo/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center pt-14 pb-20 lg:pt-20 lg:pb-28">
        {/* copy */}
        <div className="lg:col-span-6">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <span className="inline-flex items-center gap-2 rounded-full border border-dash-line bg-white px-4 py-1.5 text-xs font-semibold text-dash-ink shadow-sm">
              <span className="h-2 w-2 rounded-full bg-dash-teal animate-pulse" />
              Physiotherapy · Speech & Hearing · Audiology
            </span>
          </motion.div>

          <h1 className="mt-7 font-display font-bold tracking-tight text-dash-ink text-5xl sm:text-6xl lg:text-7xl leading-[0.98]">
            <HeroLine delay={0.15}>Move Better.</HeroLine>
            <HeroLine delay={0.3} className="text-dash-teal">Speak Better.</HeroLine>
            <HeroLine delay={0.45}>Hear Better.</HeroLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-7 max-w-xl text-lg leading-relaxed text-dash-muted"
          >
            Comprehensive physiotherapy, speech therapy and hearing care for children and adults, delivered with personalized attention and compassionate care.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <Link to="/contact" data-testid="hero-book-btn" className="group inline-flex items-center justify-center gap-2 rounded-full bg-dash-teal px-7 py-4 font-semibold text-white shadow-xl shadow-dash-teal/25 transition-all duration-300 hover:bg-dash-ink hover:-translate-y-0.5">
              Book an Appointment
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <a href={whatsappLink()} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-dash-line bg-white px-7 py-4 font-semibold text-dash-ink transition-all duration-300 hover:border-dash-teal hover:-translate-y-0.5">
              <MessageCircle className="h-5 w-5 text-[#25D366]" />
              WhatsApp Us
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 1 }}
            className="mt-8 inline-flex items-center gap-2 text-dash-ink"
          >
            <MapPin className="h-5 w-5 text-dash-gold" />
            <span className="font-medium">{CLINIC.addressShort}</span>
          </motion.div>
        </div>

        {/* visual */}
        <div className="lg:col-span-6 relative">
          <div className="relative mx-auto max-w-md lg:max-w-none">
            <motion.div style={{ y: y1 }} className="relative z-10 overflow-hidden rounded-[2.5rem] shadow-[0_40px_80px_rgb(10,37,64,0.18)]">
              <img src={STOCK.heroPhysio} alt="Physiotherapist guiding a patient through rehabilitation" className="h-[420px] lg:h-[560px] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-dash-ink/30 to-transparent" />
            </motion.div>

            {/* floating chips */}
            <motion.div
              style={{ y: y2 }}
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.1, duration: 0.6 }}
              className="absolute -left-4 lg:-left-10 top-16 z-20 flex items-center gap-3 rounded-2xl bg-white/90 backdrop-blur-xl p-4 shadow-xl border border-white/60"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-dash-teal/10 text-dash-teal"><Activity className="h-5 w-5" /></span>
              <div>
                <div className="font-display font-bold text-dash-ink leading-tight">Physiotherapy</div>
                <div className="text-xs text-dash-muted">Pain relief & rehab</div>
              </div>
            </motion.div>

            <motion.div
              style={{ y: y1 }}
              initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 1.25, duration: 0.6 }}
              className="absolute -right-3 lg:-right-8 bottom-14 z-20 flex items-center gap-3 rounded-2xl bg-white/90 backdrop-blur-xl p-4 shadow-xl border border-white/60"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-dash-indigo/10 text-dash-indigo"><Ear className="h-5 w-5" /></span>
              <div>
                <div className="font-display font-bold text-dash-ink leading-tight">Speech & Hearing</div>
                <div className="text-xs text-dash-muted">Assessment & therapy</div>
              </div>
            </motion.div>

            <div className="pointer-events-none absolute -bottom-6 left-1/2 -translate-x-1/2 h-40 w-4/5 rounded-full bg-dash-ink/10 blur-2xl" />
          </div>
        </div>
      </div>

      {/* trust strip */}
      <div className="relative border-t border-dash-line bg-white">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-dash-line">
          {["Personalized Care", "Professional Expertise", "Patient-Centered Treatment"].map((t) => (
            <div key={t} className="flex items-center justify-center gap-3 py-6 text-center">
              <span className="h-2 w-2 rounded-full bg-dash-teal" />
              <span className="font-display text-lg font-semibold text-dash-ink">{t}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
